import { ErrorBoundary } from '@sentry/react'
import { UseQueryResult } from '@tanstack/react-query'
import React from 'react'
import { LuX } from 'react-icons/lu'

import GlobalSearchResults from '@/components/misc/GlobalSearch/components/GlobalSearchResults'
import { useGlobalSearch } from '@/components/misc/GlobalSearch/hooks/useGlobalSearch'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Command, CommandInput, CommandList } from '@/components/ui/command'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog'
import { Kbd } from '@/components/ui/kbd'
import { Spinner } from '@/components/ui/spinner'
import { GlobalSearchResultsItem } from '@/types/responses/companies'

interface Props {
  globalSearchQuery: UseQueryResult<GlobalSearchResultsItem>
  isDebouncing: boolean
}

const GlobalSearchDialog: React.FC<Props> = ({
  globalSearchQuery,
  isDebouncing,
}) => {
  const globalSearch = useGlobalSearch()

  return (
    <Dialog
      open={globalSearch.isShowing}
      onOpenChange={(open) =>
        open ? globalSearch.show() : globalSearch.hide()
      }
    >
      <DialogContent
        showCloseButton={false}
        className="top-[15%] translate-y-0 overflow-hidden p-0 shadow-xl border"
        // Don't return focus to the trigger on close — its onFocus would
        // immediately reopen the palette.
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <DialogTitle className="sr-only">Search</DialogTitle>

        <DialogDescription className="sr-only">
          Search referrals, clients or companies
        </DialogDescription>

        {/* Search is server-side, so we disable cmdk's own filtering. */}
        <Command shouldFilter={false}>
          <CommandInput
            autoFocus
            placeholder="Search referrals, clients or companies..."
            value={globalSearch.searchTerm}
            onValueChange={globalSearch.setSearchTerm}
          />

          <CommandList className="max-h-[65vh]">
            <ErrorBoundary
              fallback={
                <div className="mx-2">
                  <Alert variant="critical" className="pop-in">
                    <AlertDescription>
                      <p className="mb-0">
                        Sorry, it looks like we're having a problem with this
                        search. Our team has been notified and will attempt to
                        fix the issue as soon as possible. Please try again
                        later.
                      </p>
                    </AlertDescription>
                  </Alert>
                </div>
              }
            >
              <GlobalSearchResults
                globalSearchQuery={globalSearchQuery}
                isDebouncing={isDebouncing}
              />
            </ErrorBoundary>
          </CommandList>

          {(globalSearchQuery.isFetching || isDebouncing) && (
            <div className="flex justify-center items-center p-3 h-11">
              <Spinner className="size-5" />
            </div>
          )}

          {globalSearch.searchTerm !== '' && (
            <div
              className="text-muted-foreground flex items-center justify-center
                gap-4 border-t px-3 py-2 text-xs bg-gray-200 mt-3"
            >
              <span className="flex items-center gap-1.5">
                <Kbd>↑</Kbd>
                <Kbd>↓</Kbd>
                Navigate
              </span>

              <span className="flex items-center gap-1.5">
                <Kbd>↵</Kbd>
                Select
              </span>

              <span className="flex items-center gap-1.5">
                <Kbd>Esc</Kbd>
                Close
              </span>
            </div>
          )}
        </Command>

        {/* Rendered last so the CommandInput is the first focusable element and
            takes focus when the dialog opens (it's absolutely positioned, so
            DOM order doesn't affect layout). */}
        <DialogClose asChild className="absolute top-4 right-3 size-8">
          <Button variant="ghost" size="icon">
            <LuX />
          </Button>
        </DialogClose>
      </DialogContent>
    </Dialog>
  )
}

export default GlobalSearchDialog
